/**
 * Add-to-cart for Sky sections, wired into Horizon's cart events so the cart
 * drawer, cart bubble and cart page refresh exactly like a native add.
 *
 * Usage: <button data-sky-add-to-cart="{{ variant.id }}" [data-quantity="1"]>
 *    or: <form data-sky-product-form> with an `id` (variant) and optional `quantity` field.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @param {HTMLElement} source - element dispatching the events (must be in the document)
 * @param {FormData} formData - must contain `id`; `quantity` defaults to 1
 */
export async function skyAddToCart(source, formData) {
  if (!formData.get('quantity')) formData.set('quantity', '1');

  // Ask Shopify to render cart UIs in the same request (Horizon cart items + the Sky cart drawer).
  const sectionIds = [
    ...[...document.querySelectorAll('cart-items-component')].map((el) => el instanceof HTMLElement && el.dataset.sectionId),
    ...[...document.querySelectorAll('[data-cart-section]')].map((el) => el instanceof HTMLElement && el.dataset.cartSection),
  ].filter(Boolean);
  if (sectionIds.length) formData.set('sections', sectionIds.join(','));

  const itemCount = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity: itemCount }],
      promise: deferred.promise,
    })
  );

  const refreshCart = () =>
    fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
    }).then((r) => r.json());

    const ajaxCart = await refreshCart();
    const didError = Boolean(response.status);

    if (didError) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: response.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'sky-add-to-cart',
        sourceId: source.id || 'sky',
        itemCount,
        sections: didError ? undefined : response.sections,
        didError,
      },
    });

    return { ok: !didError, message: response.description || response.message };
  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(
      new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
    );
    return { ok: false, message: error?.message };
  }
}

/** @param {HTMLElement} el @param {boolean} busy */
function setBusy(el, busy) {
  el.toggleAttribute('aria-busy', busy);
  if (el instanceof HTMLButtonElement) el.disabled = busy;
}

document.addEventListener('click', async (event) => {
  const button = /** @type {HTMLElement | null} */ (
    event.target instanceof Element ? event.target.closest('[data-sky-add-to-cart]') : null
  );
  if (!button) return;
  event.preventDefault();

  const formData = new FormData();
  formData.set('id', button.dataset.skyAddToCart || '');
  formData.set('quantity', button.dataset.quantity || '1');

  setBusy(button, true);
  await skyAddToCart(button, formData);
  setBusy(button, false);
});

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-sky-product-form')) return;
  event.preventDefault();

  const submit = /** @type {HTMLElement | null} */ (form.querySelector('[type="submit"]'));
  const error = form.querySelector('[data-sky-form-error]');
  if (submit) setBusy(submit, true);
  if (error) error.textContent = '';

  const result = await skyAddToCart(form, new FormData(form));

  if (submit) setBusy(submit, false);
  if (!result.ok && error) error.textContent = result.message || '';
});
